import { useState, useEffect } from 'react';

import { useAuthToken } from '@/hooks/useAuthToken';
import { supabaseApi } from '@/lib/api/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Mail,
  Phone,
  ChevronDown,
  Plus,
  X,
  Loader2,
  Search,
  Send,
  Trash2,
  Users,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  CheckCircle2,
  Clock,
  Link2,
  Eye,
  EyeOff,
  Copy,
  Check,
  User,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import toast from 'react-hot-toast';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';

interface AuthUsersTabProps {
  projectId: string;
}

interface AuthConfig {
  providers: {
    email: boolean;
    phone: boolean;
    google: boolean;
  };
  site_url: string;
  uri_allow_list: string;
  disable_signup: boolean;
  external_anonymous_users_enabled: boolean;
  mailer_autoconfirm?: boolean;
  // Email settings
  mailer_secure_email_change_enabled?: boolean;
  mailer_otp_exp?: number;
  mailer_otp_length?: number;
  security_update_password_require_reauthentication?: boolean;
  password_hibp_enabled?: boolean;
  password_min_length?: number;
  password_required_characters?: string;
  // Phone settings
  external_phone_enabled?: boolean;
  sms_provider?: string;
  sms_autoconfirm?: boolean;
  sms_otp_exp?: number;
  sms_otp_length?: number;
  sms_template?: string;
  sms_test_otp?: string;
  sms_twilio_account_sid?: string;
  sms_twilio_auth_token?: string;
  sms_twilio_message_service_sid?: string;
  sms_twilio_verify_service_sid?: string;
  sms_twilio_content_sid?: string;
  sms_messagebird_access_key?: string;
  sms_messagebird_originator?: string;
  sms_textlocal_api_key?: string;
  sms_textlocal_sender?: string;
  sms_vonage_api_key?: string;
  sms_vonage_api_secret?: string;
  sms_vonage_from?: string;
  // Google settings
  external_google_enabled?: boolean;
  external_google_client_id?: string;
  external_google_secret?: string;
  external_google_skip_nonce_check?: boolean;
  api_url?: string;
}

interface User {
  id: string;
  email?: string;
  phone?: string;
  created_at: string;
  updated_at?: string;
  last_sign_in_at?: string;
  email_confirmed_at?: string;
  phone_confirmed_at?: string;
  invited_at?: string;
  confirmation_sent_at?: string;
  app_metadata?: {
    provider?: string;
    [key: string]: unknown;
  };
  user_metadata?: Record<string, unknown>;
}

// Google "G" logo component
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function AuthUsersTab({ projectId }: AuthUsersTabProps) {
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();

  // Auth state
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [siteUrl, setSiteUrl] = useState('');
  const [allowedUrls, setAllowedUrls] = useState<string[]>([]);
  const [newUrl, setNewUrl] = useState('');

  // Expanded providers state
  const [expandedProvider, setExpandedProvider] = useState<'email' | 'phone' | 'google' | null>(null);

  // Provider-specific form state
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [showVonageSecret, setShowVonageSecret] = useState(false);
  const [googleSecretInput, setGoogleSecretInput] = useState('');
  const [copiedCallback, setCopiedCallback] = useState(false);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    phone: '',
    email_confirm: true,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    if (token) {
      loadAuthConfig();
    }
  }, [projectId, token]);

  useEffect(() => {
    if (token) {
      loadUsers();
    }
  }, [projectId, token, page]);

  useEffect(() => {
    if (authConfig) {
      setSiteUrl(authConfig.site_url);
      setAllowedUrls(parseUriAllowList(authConfig.uri_allow_list));
    }
  }, [authConfig]);

  const loadAuthConfig = async () => {
    if (!token) return;
    setAuthLoading(true);
    try {
      const config = await supabaseApi.getAuthConfig(token, projectId);
      setAuthConfig(config);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load auth config: ${errorMessage}`);
    } finally {
      setAuthLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!token) return;
    setUsersLoading(true);
    try {
      const response = await supabaseApi.listUsers(token, projectId, page, perPage);
      if (Array.isArray(response)) {
        setUsers(response);
        setTotalUsers(response.length);
      } else if (response.users) {
        setUsers(response.users);
        setTotalUsers(response.total || response.users.length);
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load users: ${errorMessage}`);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const updateConfig = async (updates: Record<string, unknown>) => {
    if (!token) return;
    setUpdating(true);
    try {
      const updated = await supabaseApi.updateAuthConfig(token, projectId, updates);
      setAuthConfig(updated);
      toast.success('Settings updated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to update: ${errorMessage}`);
    } finally {
      setUpdating(false);
    }
  };

  const toggleDisableSignup = () => {
    if (!authConfig) return;
    updateConfig({ disable_signup: !authConfig.disable_signup });
  };

  const toggleAnonymousUsers = () => {
    if (!authConfig) return;
    updateConfig({ external_anonymous_users_enabled: !authConfig.external_anonymous_users_enabled });
  };

  const toggleEmailAutoConfirm = () => {
    if (!authConfig) return;
    updateConfig({ mailer_autoconfirm: !authConfig.mailer_autoconfirm });
  };

  const updateSiteUrl = async () => {
    if (!authConfig || siteUrl === authConfig.site_url) return;
    await updateConfig({ site_url: siteUrl });
  };

  const updateUriAllowList = async (urls: string[]) => {
    const uriList = urls.map((url) => `${url}/**`).join(',');
    await updateConfig({ uri_allow_list: uriList });
  };

  const addAllowedUrl = () => {
    if (!newUrl.trim()) return;
    const url = newUrl.trim().replace(/\/\*\*$/, '');
    if (!url.startsWith('https://')) {
      toast.error('URL must start with https://');
      return;
    }
    const updated = [...allowedUrls, url];
    setAllowedUrls(updated);
    setNewUrl('');
    updateUriAllowList(updated);
  };

  const removeAllowedUrl = (index: number) => {
    const updated = allowedUrls.filter((_, i) => i !== index);
    setAllowedUrls(updated);
    updateUriAllowList(updated);
  };

  const parseUriAllowList = (list: string): string[] => {
    if (!list) return [];
    return list.split(',').map((url) => url.trim().replace(/\/\*\*$/, '')).filter(Boolean);
  };

  // User actions
  const handleCreateUser = async () => {
    if (!token) return;
    if (!newUser.email || !newUser.password) {
      toast.error('Email and password are required');
      return;
    }
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreating(true);
    try {
      await supabaseApi.createUser(token, projectId, {
        email: newUser.email,
        password: newUser.password,
        phone: newUser.phone || undefined,
        email_confirm: newUser.email_confirm,
      });
      toast.success('User created');
      setShowCreateModal(false);
      setNewUser({ email: '', password: '', phone: '', email_confirm: true });
      loadUsers();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to create user: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!token || !userToDelete) return;
    setDeleting(true);
    try {
      await supabaseApi.deleteUser(token, projectId, userToDelete.id);
      toast.success('User deleted');
      setShowDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to delete user: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleInviteUser = async () => {
    if (!token || !inviteEmail) {
      toast.error('Email is required');
      return;
    }
    setInviting(true);
    try {
      await supabaseApi.inviteUser(token, projectId, { email: inviteEmail });
      toast.success('Invitation sent');
      setShowInviteModal(false);
      setInviteEmail('');
      loadUsers();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to send invitation: ${errorMessage}`);
    } finally {
      setInviting(false);
    }
  };

  const handleSendMagicLink = async (user: User) => {
    if (!token || !user.email) return;
    try {
      await supabaseApi.sendMagicLink(token, projectId, user.email);
      toast.success(`Magic link sent to ${user.email}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to send magic link: ${errorMessage}`);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.id?.toLowerCase().includes(query) ||
      user.phone?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(totalUsers / perPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const copyCallbackUrl = async () => {
    if (!authConfig?.api_url) return;
    const url = `${authConfig.api_url}/auth/v1/callback`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCallback(true);
      setTimeout(() => setCopiedCallback(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!authConfig) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Users className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm">Authentication not configured</p>
      </div>
    );
  }

  const providers = [
    {
      id: 'email' as const,
      name: 'Email',
      description: 'Sign in with email address and password',
      icon: Mail,
      enabled: authConfig.providers.email,
    },
    {
      id: 'phone' as const,
      name: 'Phone',
      description: 'Sign in with phone number via SMS',
      icon: Phone,
      enabled: authConfig.providers.phone,
    },
    {
      id: 'google' as const,
      name: 'Google',
      description: 'Sign in with Google account',
      icon: GoogleIcon,
      enabled: authConfig.providers.google,
    },
  ];

  const smsProvider = authConfig.sms_provider || 'twilio';
  const isTwilio = smsProvider === 'twilio' || smsProvider === 'twilio_verify';

  return (
    <div className={cn("h-full", isMobile ? "flex flex-col gap-6 overflow-y-auto" : "flex gap-6")}>
      {/* Left Column - Auth Settings (45% on desktop, full width on mobile) */}
      <div className={cn(
        isMobile ? "flex-shrink-0" : "w-[45%] h-full flex-shrink-0 overflow-y-auto pr-2"
      )}>
        <div className="space-y-5">
        {/* Sign-in Methods */}
        <section>
          <h2 className="text-sm font-medium mb-3">Sign-in Methods</h2>
          <div className="space-y-2">
            {providers.map((provider) => {
              const Icon = provider.icon;
              const isExpanded = expandedProvider === provider.id;

              return (
                <Collapsible
                  key={provider.id}
                  open={isExpanded}
                  onOpenChange={(open) => setExpandedProvider(open ? provider.id : null)}
                >
                  <div className="rounded-lg border border-border bg-card overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <button
                        disabled={updating}
                        className="w-full flex items-center gap-3 p-3 hover:bg-accent/50 transition-colors text-left disabled:opacity-50"
                      >
                        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{provider.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{provider.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded ${
                              provider.enabled
                                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {provider.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="px-4 pb-4 pt-3 border-t border-border/50 space-y-4">
                      {/* Email Provider Config */}
                      {provider.id === 'email' && (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Enable email sign-in</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Allow users to sign in with email and password</p>
                            </div>
                            <Switch
                              checked={authConfig.providers.email}
                              onCheckedChange={(checked) => updateConfig({ external_email_enabled: checked })}
                              disabled={updating}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Confirm email</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Users must verify their email before signing in</p>
                            </div>
                            <Switch
                              checked={!authConfig.mailer_autoconfirm}
                              onCheckedChange={(checked) => updateConfig({ mailer_autoconfirm: !checked })}
                              disabled={updating}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Secure email change</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Require confirmation from both old and new email</p>
                            </div>
                            <Switch
                              checked={authConfig.mailer_secure_email_change_enabled ?? true}
                              onCheckedChange={(checked) => updateConfig({ mailer_secure_email_change_enabled: checked })}
                              disabled={updating}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Secure password change</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Users must be recently logged in to change password</p>
                            </div>
                            <Switch
                              checked={authConfig.security_update_password_require_reauthentication ?? false}
                              onCheckedChange={(checked) => updateConfig({ security_update_password_require_reauthentication: checked })}
                              disabled={updating}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Prevent leaked passwords</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Reject known compromised passwords via HaveIBeenPwned</p>
                            </div>
                            <Switch
                              checked={authConfig.password_hibp_enabled ?? false}
                              onCheckedChange={(checked) => updateConfig({ password_hibp_enabled: checked })}
                              disabled={updating}
                            />
                          </div>

                          <div className="pt-2 border-t border-border/50">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-medium">Min password length</Label>
                                <Input
                                  type="number"
                                  min={6}
                                  max={72}
                                  value={authConfig.password_min_length ?? 6}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (!isNaN(value)) {
                                      updateConfig({ password_min_length: Math.min(72, Math.max(6, value)) });
                                    }
                                  }}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium">Password requirements</Label>
                                <Select
                                  value={authConfig.password_required_characters ?? 'none'}
                                  onValueChange={(value) => updateConfig({ password_required_characters: value })}
                                  disabled={updating}
                                >
                                  <SelectTrigger className="mt-1 h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    <SelectItem value="letters_digits">Letters + digits</SelectItem>
                                    <SelectItem value="lower_upper_letters_digits">Mixed case + digits</SelectItem>
                                    <SelectItem value="lower_upper_letters_digits_symbols">Mixed case + digits + symbols</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-border/50">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-medium">OTP expiration (seconds)</Label>
                                <Input
                                  type="number"
                                  min={60}
                                  max={86400}
                                  value={authConfig.mailer_otp_exp ?? 3600}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (!isNaN(value)) {
                                      updateConfig({ mailer_otp_exp: Math.min(86400, Math.max(60, value)) });
                                    }
                                  }}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium">OTP length</Label>
                                <Input
                                  type="number"
                                  min={6}
                                  max={10}
                                  value={authConfig.mailer_otp_length ?? 6}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (!isNaN(value)) {
                                      updateConfig({ mailer_otp_length: Math.min(10, Math.max(6, value)) });
                                    }
                                  }}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Phone Provider Config */}
                      {provider.id === 'phone' && (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Enable phone sign-in</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Allow users to sign in with phone number via SMS</p>
                            </div>
                            <Switch
                              checked={authConfig.external_phone_enabled ?? false}
                              onCheckedChange={(checked) => updateConfig({ external_phone_enabled: checked })}
                              disabled={updating}
                            />
                          </div>

                          <div>
                            <Label className="text-xs font-medium">SMS Provider</Label>
                            <Select
                              value={smsProvider}
                              onValueChange={(value) => updateConfig({ sms_provider: value })}
                              disabled={updating}
                            >
                              <SelectTrigger className="mt-1 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="twilio">Twilio</SelectItem>
                                <SelectItem value="twilio_verify">Twilio Verify</SelectItem>
                                <SelectItem value="messagebird">MessageBird</SelectItem>
                                <SelectItem value="textlocal">Textlocal</SelectItem>
                                <SelectItem value="vonage">Vonage</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Twilio Config */}
                          {isTwilio && (
                            <div className="space-y-3 pt-2 border-t border-border/50">
                              <div>
                                <Label className="text-xs font-medium">Twilio Account SID</Label>
                                <Input
                                  value={authConfig.sms_twilio_account_sid ?? ''}
                                  onChange={(e) => updateConfig({ sms_twilio_account_sid: e.target.value })}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm font-mono"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium">Twilio Auth Token</Label>
                                <div className="relative mt-1">
                                  <Input
                                    type={showTwilioToken ? 'text' : 'password'}
                                    value={authConfig.sms_twilio_auth_token ?? ''}
                                    onChange={(e) => updateConfig({ sms_twilio_auth_token: e.target.value })}
                                    disabled={updating}
                                    className="h-8 text-sm font-mono pr-8"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowTwilioToken(!showTwilioToken)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                  >
                                    {showTwilioToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              </div>
                              {smsProvider === 'twilio' && (
                                <div>
                                  <Label className="text-xs font-medium">Message Service SID</Label>
                                  <Input
                                    value={authConfig.sms_twilio_message_service_sid ?? ''}
                                    onChange={(e) => updateConfig({ sms_twilio_message_service_sid: e.target.value })}
                                    disabled={updating}
                                    className="mt-1 h-8 text-sm font-mono"
                                  />
                                </div>
                              )}
                              {smsProvider === 'twilio_verify' && (
                                <div>
                                  <Label className="text-xs font-medium">Verify Service SID</Label>
                                  <Input
                                    value={authConfig.sms_twilio_verify_service_sid ?? ''}
                                    onChange={(e) => updateConfig({ sms_twilio_verify_service_sid: e.target.value })}
                                    placeholder="VA..."
                                    disabled={updating}
                                    className="mt-1 h-8 text-sm font-mono"
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {/* MessageBird Config */}
                          {smsProvider === 'messagebird' && (
                            <div className="space-y-3 pt-2 border-t border-border/50">
                              <div>
                                <Label className="text-xs font-medium">Access Key</Label>
                                <Input
                                  value={authConfig.sms_messagebird_access_key ?? ''}
                                  onChange={(e) => updateConfig({ sms_messagebird_access_key: e.target.value })}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm font-mono"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium">Originator</Label>
                                <Input
                                  value={authConfig.sms_messagebird_originator ?? ''}
                                  onChange={(e) => updateConfig({ sms_messagebird_originator: e.target.value })}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                            </div>
                          )}

                          {/* Textlocal Config */}
                          {smsProvider === 'textlocal' && (
                            <div className="space-y-3 pt-2 border-t border-border/50">
                              <div>
                                <Label className="text-xs font-medium">API Key</Label>
                                <Input
                                  value={authConfig.sms_textlocal_api_key ?? ''}
                                  onChange={(e) => updateConfig({ sms_textlocal_api_key: e.target.value })}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm font-mono"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium">Sender</Label>
                                <Input
                                  value={authConfig.sms_textlocal_sender ?? ''}
                                  onChange={(e) => updateConfig({ sms_textlocal_sender: e.target.value })}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                            </div>
                          )}

                          {/* Vonage Config */}
                          {smsProvider === 'vonage' && (
                            <div className="space-y-3 pt-2 border-t border-border/50">
                              <div>
                                <Label className="text-xs font-medium">API Key</Label>
                                <Input
                                  value={authConfig.sms_vonage_api_key ?? ''}
                                  onChange={(e) => updateConfig({ sms_vonage_api_key: e.target.value })}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm font-mono"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-medium">API Secret</Label>
                                <div className="relative mt-1">
                                  <Input
                                    type={showVonageSecret ? 'text' : 'password'}
                                    value={authConfig.sms_vonage_api_secret ?? ''}
                                    onChange={(e) => updateConfig({ sms_vonage_api_secret: e.target.value })}
                                    disabled={updating}
                                    className="h-8 text-sm font-mono pr-8"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowVonageSecret(!showVonageSecret)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                  >
                                    {showVonageSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs font-medium">From</Label>
                                <Input
                                  value={authConfig.sms_vonage_from ?? ''}
                                  onChange={(e) => updateConfig({ sms_vonage_from: e.target.value })}
                                  disabled={updating}
                                  className="mt-1 h-8 text-sm"
                                />
                              </div>
                            </div>
                          )}

                          {/* SMS Settings */}
                          <div className="pt-2 border-t border-border/50">
                            <div className="flex items-center justify-between mb-3">
                              <div className="pr-4">
                                <p className="text-sm font-medium">Phone confirmations</p>
                                <p className="text-xs text-muted-foreground mt-0.5">Users must verify phone before signing in</p>
                              </div>
                              <Switch
                                checked={!authConfig.sms_autoconfirm}
                                onCheckedChange={(checked) => updateConfig({ sms_autoconfirm: !checked })}
                                disabled={updating}
                              />
                            </div>

                            {smsProvider !== 'twilio_verify' && (
                              <>
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                  <div>
                                    <Label className="text-xs font-medium">OTP expiry (seconds)</Label>
                                    <Input
                                      type="number"
                                      min={30}
                                      max={86400}
                                      value={authConfig.sms_otp_exp ?? 60}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (!isNaN(value)) {
                                          updateConfig({ sms_otp_exp: Math.min(86400, Math.max(30, value)) });
                                        }
                                      }}
                                      disabled={updating}
                                      className="mt-1 h-8 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs font-medium">OTP length</Label>
                                    <Input
                                      type="number"
                                      min={6}
                                      max={10}
                                      value={authConfig.sms_otp_length ?? 6}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (!isNaN(value)) {
                                          updateConfig({ sms_otp_length: Math.min(10, Math.max(6, value)) });
                                        }
                                      }}
                                      disabled={updating}
                                      className="mt-1 h-8 text-sm"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs font-medium">SMS template</Label>
                                  <Textarea
                                    value={authConfig.sms_template ?? 'Your code is {{ .Code }}'}
                                    onChange={(e) => updateConfig({ sms_template: e.target.value })}
                                    disabled={updating}
                                    className="mt-1 text-sm min-h-[60px]"
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Use <code className="bg-muted px-1 py-0.5 rounded text-xs">{`{{ .Code }}`}</code> for OTP
                                  </p>
                                </div>
                              </>
                            )}
                          </div>

                          <div className="pt-2 border-t border-border/50">
                            <Label className="text-xs font-medium">Test phone numbers</Label>
                            <Input
                              value={authConfig.sms_test_otp ?? ''}
                              onChange={(e) => updateConfig({ sms_test_otp: e.target.value })}
                              placeholder="18005550123=789012"
                              disabled={updating}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Format: phone=otp pairs, comma separated
                            </p>
                          </div>
                        </>
                      )}

                      {/* Google Provider Config */}
                      {provider.id === 'google' && (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Enable Google sign-in</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Allow OAuth, One Tap, Android apps, and Chrome extensions</p>
                            </div>
                            <Switch
                              checked={authConfig.external_google_enabled ?? false}
                              onCheckedChange={(checked) => updateConfig({ external_google_enabled: checked })}
                              disabled={updating}
                            />
                          </div>

                          <div>
                            <Label className="text-xs font-medium">Client ID(s)</Label>
                            <Input
                              value={authConfig.external_google_client_id ?? ''}
                              onChange={(e) => updateConfig({ external_google_client_id: e.target.value })}
                              placeholder="Comma-separated for multiple clients"
                              disabled={updating}
                              className="mt-1 h-8 text-sm"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              For Web OAuth, Android apps, One Tap, Chrome extensions
                            </p>
                          </div>

                          <div>
                            <Label className="text-xs font-medium">Client Secret (for OAuth)</Label>
                            <Input
                              type="password"
                              value={googleSecretInput}
                              onChange={(e) => setGoogleSecretInput(e.target.value)}
                              onBlur={() => {
                                if (googleSecretInput.trim()) {
                                  updateConfig({ external_google_secret: googleSecretInput });
                                }
                              }}
                              placeholder={authConfig.external_google_secret ? '••••••••' : 'Enter secret'}
                              disabled={updating}
                              className="mt-1 h-8 text-sm"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <p className="text-sm font-medium">Skip nonce checks</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Accept ID tokens with any nonce (useful for iOS)</p>
                            </div>
                            <Switch
                              checked={authConfig.external_google_skip_nonce_check ?? false}
                              onCheckedChange={(checked) => updateConfig({ external_google_skip_nonce_check: checked })}
                              disabled={updating}
                            />
                          </div>

                          {authConfig.api_url && (
                            <div className="pt-2 border-t border-border/50">
                              <Label className="text-xs font-medium">Callback URL</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  value={`${authConfig.api_url}/auth/v1/callback`}
                                  readOnly
                                  className="h-8 text-xs font-mono bg-muted/30 flex-1"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={copyCallbackUrl}
                                  className="h-8 w-8 p-0"
                                >
                                  {copiedCallback ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Add this to your Google OAuth console
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
        </section>

        {/* General Settings */}
        <section>
          <h2 className="text-sm font-medium mb-3">General Settings</h2>
          <div className="rounded-lg border border-border bg-card divide-y divide-border">
            <div className="flex items-center justify-between p-3">
              <div className="pr-3">
                <p className="text-sm font-medium">Allow new signups</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When disabled, only invited users can create accounts
                </p>
              </div>
              <Switch
                checked={!authConfig.disable_signup}
                onCheckedChange={toggleDisableSignup}
                disabled={updating}
              />
            </div>

            <div className="flex items-center justify-between p-3">
              <div className="pr-3">
                <p className="text-sm font-medium">Anonymous sign-in</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Allow users to use your app without creating an account
                </p>
              </div>
              <Switch
                checked={authConfig.external_anonymous_users_enabled}
                onCheckedChange={toggleAnonymousUsers}
                disabled={updating}
              />
            </div>

            <div className="flex items-center justify-between p-3">
              <div className="pr-3">
                <p className="text-sm font-medium">Require email confirmation</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Users must verify their email before signing in
                </p>
              </div>
              <Switch
                checked={!authConfig.mailer_autoconfirm}
                onCheckedChange={toggleEmailAutoConfirm}
                disabled={updating}
              />
            </div>
          </div>

          {/* Advanced */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Link2 className="w-3.5 h-3.5" />
            <span>Advanced settings</span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
          </button>

          {showAdvanced && (
            <div className="mt-3 rounded-lg border border-border bg-card divide-y divide-border">
              <div className="p-3">
                <label className="block text-sm font-medium mb-1">Site URL</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Base URL for email redirects and OAuth
                </p>
                <div className="flex gap-2">
                  <Input
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    className="flex-1 h-8 text-sm"
                    placeholder="https://your-app.com"
                  />
                  <Button
                    onClick={updateSiteUrl}
                    disabled={updating || siteUrl === authConfig.site_url}
                    size="sm"
                    className="h-8"
                  >
                    Save
                  </Button>
                </div>
              </div>

              <div className="p-3">
                <label className="block text-sm font-medium mb-1">Redirect URLs</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Allowed URLs for OAuth redirects
                </p>
                <div className="space-y-2">
                  {allowedUrls.map((url, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={`${url}/**`} readOnly className="flex-1 h-8 text-xs bg-muted/50 text-muted-foreground" />
                      <Button
                        onClick={() => removeAllowedUrl(i)}
                        disabled={updating}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addAllowedUrl()}
                      className="flex-1 h-8 text-sm"
                      placeholder="https://example.com"
                    />
                    <Button onClick={addAllowedUrl} disabled={updating || !newUrl.trim()} size="sm" className="h-8 w-8 p-0">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
        </div>
      </div>

      {/* Right Column - Users (55% on desktop, full width on mobile) */}
      <div className={cn(
        "flex-1 min-w-0 flex flex-col rounded-lg border border-border bg-card",
        isMobile ? "min-h-[400px]" : "h-full"
      )}>
        <div className="flex items-center justify-between p-4 pb-3">
          <div>
            <h2 className="text-sm font-medium">Users</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalUsers} registered
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowInviteModal(true)} variant="outline" size="sm" className="h-8">
              <Send className="w-3.5 h-3.5 mr-1.5" />
              Invite
            </Button>
            <Button onClick={() => setShowCreateModal(true)} size="sm" className="h-8">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Create
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 px-4 pb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email, phone, or ID..."
              className="pl-9 h-9"
            />
          </div>
          <Button onClick={loadUsers} variant="ghost" size="sm" className="h-9 w-9 p-0">
            <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Users List */}
        <div className="border-t border-border overflow-hidden flex-1 flex flex-col">
          {usersLoading && users.length === 0 ? (
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground">
              <Users className="w-10 h-10 mb-2 opacity-40" />
              <p className="text-sm">{searchQuery ? 'No users found' : 'No users yet'}</p>
            </div>
          ) : (
            <div className="divide-y divide-border flex-1 overflow-y-auto">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-accent/30 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedUser(user);
                    setShowUserDetailsModal(true);
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-muted text-muted-foreground">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {user.email || user.phone || 'Anonymous'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{user.app_metadata?.provider || 'email'}</span>
                        {(user.email_confirmed_at || user.phone_confirmed_at) && (
                          <span className="flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-3 h-3" />
                            verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" />
                      {formatDate(user.created_at)}
                    </span>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        {user.email && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendMagicLink(user);
                            }}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Magic link
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserToDelete(user);
                            setShowDeleteModal(true);
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-muted/30">
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className={cn(
          "sm:max-w-md",
          isMobile && "max-w-[95vw] max-h-[85vh] overflow-y-auto"
        )}>
          <DialogHeader>
            <DialogTitle>Create user</DialogTitle>
            <DialogDescription>Add a new user to your application</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="create-password">Password</Label>
              <Input
                id="create-password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Minimum 6 characters"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="create-phone">Phone (optional)</Label>
              <Input
                id="create-phone"
                type="tel"
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                placeholder="+1234567890"
                className="mt-1.5"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="create-confirm"
                checked={newUser.email_confirm}
                onCheckedChange={(checked) => setNewUser({ ...newUser, email_confirm: checked === true })}
              />
              <Label htmlFor="create-confirm" className="text-sm cursor-pointer">
                Skip email verification
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className={cn(
          "sm:max-w-md",
          isMobile && "max-w-[95vw] max-h-[85vh] overflow-y-auto"
        )}>
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
            <DialogDescription>Send an invitation email to a new user</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              className="mt-1.5"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)} disabled={inviting}>
              Cancel
            </Button>
            <Button onClick={handleInviteUser} disabled={inviting}>
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className={cn(
          "sm:max-w-md",
          isMobile && "max-w-[95vw] max-h-[85vh] overflow-y-auto"
        )}>
          <DialogHeader>
            <DialogTitle>Delete user</DialogTitle>
            <DialogDescription>This action cannot be undone</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {userToDelete && (
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm font-medium">
                  {userToDelete.email || userToDelete.phone || 'Anonymous'}
                </p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                  {userToDelete.id}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showUserDetailsModal} onOpenChange={setShowUserDetailsModal}>
        <DialogContent className={cn(
          "sm:max-w-lg",
          isMobile && "max-w-[95vw] max-h-[85vh] overflow-y-auto"
        )}>
          <DialogHeader>
            <DialogTitle>{selectedUser?.email || selectedUser?.phone || 'User details'}</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="py-2 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">User ID</p>
                  <p className="font-mono text-xs break-all">{selectedUser.id}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Provider</p>
                  <p>{selectedUser.app_metadata?.provider || 'email'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Created</p>
                  <p>{formatDateTime(selectedUser.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Last sign in</p>
                  <p>{selectedUser.last_sign_in_at ? formatDateTime(selectedUser.last_sign_in_at) : 'Never'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Email verified</p>
                  <p>{selectedUser.email_confirmed_at ? formatDateTime(selectedUser.email_confirmed_at) : 'No'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Phone verified</p>
                  <p>{selectedUser.phone_confirmed_at ? formatDateTime(selectedUser.phone_confirmed_at) : 'No'}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-2">Login methods</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Email</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${selectedUser.email ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                      {selectedUser.email ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Phone</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${selectedUser.phone ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                      {selectedUser.phone ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Google</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${selectedUser.app_metadata?.provider === 'google' || (selectedUser as any).identities?.some((i: any) => i.provider === 'google') ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                      {selectedUser.app_metadata?.provider === 'google' || (selectedUser as any).identities?.some((i: any) => i.provider === 'google') ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDetailsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
